import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { ConversationId as ConversationIdSchema } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse as ConversationListItemResponseType } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import {
  ConversationListItemResponse,
  type UpdateConversationInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  BasicIndex,
  parseWhereExpression,
  type SimpleComparison,
} from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Option } from "effect";
import { catchApiErrors } from "./error-handling";

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const DEFAULT_PAGE_SIZE = 25;

function parseWhereToSimpleComparisons(
  where: Parameters<typeof parseWhereExpression>[0],
): SimpleComparison[] {
  const result = parseWhereExpression<SimpleComparison | SimpleComparison[]>(
    where,
    {
      handlers: {
        and: (...args: (SimpleComparison | SimpleComparison[])[]) =>
          args.flat(),
        or: (...args: (SimpleComparison | SimpleComparison[])[]) => args.flat(),
        not: (inner: SimpleComparison | SimpleComparison[]) => {
          const items = Array.isArray(inner) ? inner : [inner];
          return items.map((c) => ({ ...c, operator: `not_${c.operator}` }));
        },
        eq: (field: string[], value: unknown) => ({
          field,
          operator: "eq",
          value,
        }),
        gt: (field: string[], value: unknown) => ({
          field,
          operator: "gt",
          value,
        }),
        gte: (field: string[], value: unknown) => ({
          field,
          operator: "gte",
          value,
        }),
        lt: (field: string[], value: unknown) => ({
          field,
          operator: "lt",
          value,
        }),
        lte: (field: string[], value: unknown) => ({
          field,
          operator: "lte",
          value,
        }),
        in: (first: unknown, second: unknown) => {
          if (Array.isArray(first)) {
            return { field: first, operator: "in", value: second };
          }
          return { field: second as string[], operator: "in", value: [first] };
        },
        ilike: (field: string[], value: unknown) => ({
          field,
          operator: "ilike",
          value,
        }),
        like: (field: string[], value: unknown) => ({
          field,
          operator: "like",
          value,
        }),
        isNull: (field: string[]) => ({ field, operator: "isNull" }),
        isUndefined: (field: string[]) => ({ field, operator: "isUndefined" }),
      },
    },
  );
  if (!result) {
    return [];
  }
  return Array.isArray(result) ? result : [result];
}

function toOptionIfPresent<T>(
  value: T | null | undefined,
): Option.Option<T> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === null ? Option.none() : Option.some(value);
}

function toUpdateConversationInput(
  changes: Partial<ConversationListItemResponseType>,
): UpdateConversationInput {
  const input: UpdateConversationInput = {
    status: changes.status,
    priority: toOptionIfPresent(changes.priority),
    readAt: toOptionIfPresent(changes.readAt),
    mongoAssigneeId: toOptionIfPresent(changes.mongoAssigneeId),
    mongoCustomerId: toOptionIfPresent(changes.mongoCustomerId),
    tagUpdate: changes.tagIds
      ? { _tag: "replace", tagIds: changes.tagIds }
      : undefined,
    snoozedUntil: changes.snoozedUntil,
  };
  return input;
}

export const conversationListQueryKeys = {
  list: () => ["conversations", "list"] as const,
};

export async function fetchSingleConversation(
  effectApi: EffectApiContext,
  conversationId: ConversationId,
): Promise<ConversationListItemResponseType> {
  const client = effectApi.getClient();
  return effectApi.runPromise(
    catchApiErrors(
      client.conversation.getConversation({
        path: { id: ConversationIdSchema.make(conversationId) },
      }),
    ),
  );
}

export interface CreateConversationsListCollectionOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
}

export function serializeConversation(
  conversation: ConversationListItemResponseType,
): typeof ConversationListItemResponse.Encoded {
  return {
    ...conversation,
    readAt: conversation.readAt?.toISOString() ?? null,
    lastCustomerMessageAt:
      conversation.lastCustomerMessageAt?.toISOString() ?? null,
    lastMerchantMessageAt:
      conversation.lastMerchantMessageAt?.toISOString() ?? null,
    createdAt: conversation.createdAt?.toISOString() ?? null,
    statusUpdatedAt: conversation.statusUpdatedAt?.toISOString() ?? null,
    snoozedUntil: conversation.snoozedUntil?.toISOString() ?? null,
  };
}

export function createConversationsListCollection({
  queryClient,
  effectApi,
}: CreateConversationsListCollectionOptions) {
  const collection = createCollection(
    queryCollectionOptions({
      queryKey: conversationListQueryKeys.list(),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      queryFn: async (ctx): Promise<ConversationListItemResponseType[]> => {
        const { where, limit, offset } = ctx.meta?.loadSubsetOptions ?? {};
        const filters = parseWhereToSimpleComparisons(where);

        // When queried by a single conversation ID (e.g. direct navigation to
        // /support-v2/:conversationId), fetch that conversation directly
        // instead of going through the filtered list endpoint.
        // field can be ["id"] or ["conversation", "id"] depending on query structure
        const idFilter = filters.find(
          (f) => f.field[f.field.length - 1] === "id",
        );
        if (idFilter?.operator === "eq" && typeof idFilter.value === "string") {
          const conversationId = ConversationIdSchema.make(idFilter.value);
          const conversation = await fetchSingleConversation(
            effectApi,
            conversationId,
          );
          return [conversation];
        }

        const requestedLimit = limit ?? DEFAULT_PAGE_SIZE;
        const requestedOffset = offset ?? 0;

        // Read filterGroup directly from URL (source of truth for all filters
        // including WORDS/search which can't be expressed in TanStack where)
        const searchParams = new URLSearchParams(window.location.search);
        const encodedFilterGroup = searchParams.get("fg") ?? undefined;

        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getFilteredConversations({
              urlParams: {
                limit: requestedLimit,
                offset: requestedOffset,
                filterGroup: encodedFilterGroup,
              },
            }),
          ),
        );

        return [...result.items];
      },
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();

        for (const mutation of transaction.mutations) {
          const payload = toUpdateConversationInput(mutation.changes);
          try {
            await effectApi.runPromise(
              catchApiErrors(
                client.conversation.updateConversation({
                  path: { id: ConversationIdSchema.make(mutation.key) },
                  payload,
                }),
              ),
            );
          } catch (err) {
            console.error("[snooze] PATCH failed for", mutation.key, err);
          }
        }
      },
    }),
  );

  return collection;
}
